/**
 * Hooks: dados do portal Extranet KB.
 * Todas as chamadas passam pelas RPCs kb_extranet_* (SECURITY DEFINER).
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export function useExtranetSpaces(tenantSlug: string, enabled = true) {
  return useQuery({
    queryKey: ['extranet', 'spaces', tenantSlug],
    enabled: enabled && !!tenantSlug,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('kb_extranet_list_spaces', {
        p_tenant_slug: tenantSlug,
      });
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useExtranetPages(tenantSlug: string, spaceSlug: string | undefined) {
  return useQuery({
    queryKey: ['extranet', 'pages', tenantSlug, spaceSlug],
    enabled: !!tenantSlug && !!spaceSlug,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('kb_extranet_list_pages', {
        p_tenant_slug: tenantSlug,
        p_space_slug: spaceSlug!,
      });
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useExtranetPage(
  tenantSlug: string,
  spaceSlug: string | undefined,
  pageSlug: string | undefined,
) {
  return useQuery({
    queryKey: ['extranet', 'page', tenantSlug, spaceSlug, pageSlug],
    enabled: !!tenantSlug && !!spaceSlug && !!pageSlug,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('kb_extranet_get_page', {
        p_tenant_slug: tenantSlug,
        p_space_slug: spaceSlug!,
        p_page_slug: pageSlug!,
      });
      if (error) throw error;
      return (Array.isArray(data) ? data[0] : null) ?? null;
    },
  });
}

/**
 * useExtranetMyAccesses — Lista os acessos (spaces/coleções) concedidos ao
 * usuário externo autenticado no portal. Backing RPC: kb_extranet_list_my_accesses.
 */
export interface ExtranetMyAccessRow {
  access_id: string;
  space_id: string | null;
  space_slug: string | null;
  space_name: string | null;
  collection_id: string | null;
  collection_name: string | null;
  access_level: 'read' | 'comment';
  granted_at: string;
  expires_at: string | null;
  is_expired: boolean;
}

export function useExtranetMyAccesses(tenantSlug: string, enabled = true) {
  return useQuery({
    queryKey: ['extranet', 'my-accesses', tenantSlug],
    enabled: enabled && !!tenantSlug,
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as any)('kb_extranet_list_my_accesses', {
        p_tenant_slug: tenantSlug,
      });
      if (error) throw error;
      return (data ?? []) as ExtranetMyAccessRow[];
    },
    staleTime: 30_000,
  });
}

